<template>
  <tbody class="dict-table__entry">
    <template v-if="narrowTable">
      <tr :class="['dict-table__row', 'dict-table__row--mobile-summary', isExpanded ? 'dict-mobile-entry--expanded' : '']">
        <td class="dict-table__cell dict-table__cell--mobile-form">
          <div class="dict-mobile-entry__lead">
            <button
              ref="disclosureButton"
              type="button"
              class="dict-table__expand dict-mobile-entry__disclosure"
              :aria-expanded="String(isExpanded)"
              :aria-controls="detailsId"
              :aria-label="$t(isExpanded ? 'dict.hide_details' : 'dict.show_details')"
              @click="toggleExpanded"
            >
              <i
                class="fas fa-chevron-right dict-table__expand-icon"
                :class="{ 'dict-table__expand-icon--open': isExpanded }"
                aria-hidden="true"
              ></i>
            </button>

            <div
              :id="detailsId"
              class="dict-mobile-entry__form-col"
            >
              <template v-if="!isExpanded">
                <div
                  v-if="collapsedRow"
                  :class="['dict-mobile-entry__form-line', rowClass(collapsedRow.candidates)]"
                >
                  <span class="dict-word-list">
                    <template v-for="(word, idx) in collapsedRow.genderedWords">
                      <router-link
                        :key="collapsedRow.key + '-mobile-word-' + idx"
                        :to="encodeURI('/dict/' + word.link)"
                        replace
                        :class="wordClass(word.candidates)"
                      >{{ word.text }}</router-link>
                      <span
                        v-if="idx < collapsedRow.genderedWords.length - 1"
                        :key="collapsedRow.key + '-mobile-sep-' + idx"
                        class="dict-word-sep"
                      > · </span>
                    </template>
                  </span>
                </div>
              </template>
              <template v-else>
                <div class="dict-mobile-entry__form-stack">
                  <div
                    v-for="row in rows"
                    :key="entry.id + '-mobile-form-' + row.key"
                    :class="['dict-mobile-entry__form-line', rowClass(row.candidates)]"
                  >
                    <span class="dict-table__scope" aria-hidden="true">
                      <i :class="row.icon"></i>
                    </span>
                    <span class="dict-word-list">
                      <template v-for="(word, idx) in row.genderedWords">
                        <router-link
                          :key="row.key + '-mobile-form-word-' + idx"
                          :to="encodeURI('/dict/' + word.link)"
                          replace
                          :class="wordClass(word.candidates)"
                        >{{ word.text }}</router-link>
                        <span
                          v-if="idx < row.genderedWords.length - 1"
                          :key="row.key + '-mobile-form-sep-' + idx"
                          class="dict-word-sep"
                        > · </span>
                      </template>
                    </span>
                    <span class="sr-only" v-if="scopeLabel(row)">{{ scopeLabel(row) }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </td>
        <td class="dict-table__cell dict-table__cell--mobile-alt">
          <div class="dict-mobile-entry__alt-row">
            <div class="dict-mobile-entry__alt-col">
              <div class="dict-mobile-entry__alt-stack">
                <template v-if="!isExpanded">
                  <span
                    v-if="collapsedRow"
                    :class="[
                      'dict-table__answer',
                      'dict-mobile-entry__alt-line',
                      collapsedRow.isNotation ? 'dict-table__answer--notation' : ''
                    ]"
                  >{{ collapsedRow.alternativeText }}</span>
                </template>
                <template v-else>
                  <span
                    v-for="row in rows"
                    :key="entry.id + '-mobile-alt-' + row.key"
                    :class="[
                      'dict-table__answer',
                      'dict-mobile-entry__alt-line',
                      rowClass(row.candidates),
                      row.isNotation ? 'dict-table__answer--notation' : ''
                    ]"
                  >{{ row.alternativeText }}</span>
                </template>
              </div>
            </div>

            <div class="dict-mobile-entry__rating">
              <div class="dict-vote dict-vote--table" v-if="isNeutralEntry(entry)">
                <button
                  type="button"
                  class="dict-vote__btn dict-vote__btn--up"
                  :class="{ 'dict-vote__btn--active': entry.like_by_me }"
                  :title="entry.like_by_me ? $t('this entry is useful (click again to undo)') : $t('this entry is useful')"
                  v-b-tooltip.hover.top
                  v-on="isLoggedIn() ? { click: () => $emit('like', entry.id) } : { click: () => $emit('register') }"
                >
                  <i :class="entry.like_by_me ? 'fas fa-caret-up' : 'fa fa-angle-up'"></i>
                </button>
                <span class="dict-vote__score">{{ entry.total_likes - entry.total_dislikes }}</span>
                <button
                  type="button"
                  class="dict-vote__btn"
                  :class="{ 'dict-vote__btn--active': entry.dislike_by_me }"
                  :title="entry.dislike_by_me ? $t('this entry is not useful (click again to undo)') : $t('this entry is not useful')"
                  v-b-tooltip.hover.top
                  v-on="isLoggedIn() ? { click: () => $emit('dislike', entry.id) } : { click: () => $emit('register') }"
                >
                  <i :class="entry.dislike_by_me ? 'fas fa-caret-down text-secondary' : 'fa fa-angle-down text-secondary'"></i>
                </button>
              </div>
              <div v-else-if="isNotationEntry(entry)" class="dict-notation-hint dict-notation-hint--table">
                <i
                  class="fas fa-info-circle dict-notation-hint__icon"
                  :title="$t('dict.notation_tooltip')"
                  v-b-tooltip.hover.top
                ></i>
              </div>
            </div>
          </div>
        </td>
      </tr>
      <tr v-if="isExpanded" class="dict-table__row dict-table__row--mobile-footer">
        <td class="dict-table__cell dict-table__cell--mobile-footer" colspan="2">
          <div class="dict-mobile-entry__footer">
            <div v-if="hasExample" class="dict-mobile-entry__comment">
              <i class="fas fa-quote-left dict-table__comment-icon" aria-hidden="true"></i>
              <span>{{ entry.example }}</span>
            </div>
            <div class="dict-mobile-entry__meta">
              <router-link
                v-if="hasCreator"
                class="dict-mobile-entry__creator"
                :to="{ name: 'auth.profile', params: { username: entry.editor } }"
                v-b-tooltip.hover.top
                :title="entry.edit_date || ''"
              >{{ entry.editor }}</router-link>
              <dict-entry-actions
                class="dict-mobile-entry__inline-actions"
                :entry="entry"
                layout="row"
                @add="$emit('add', $event)"
                @flag="$emit('flag', $event)"
                @register="$emit('register')"
              />
            </div>
          </div>
        </td>
      </tr>
    </template>

    <template v-else>
      <tr :class="['dict-table__row', 'dict-table__row--summary', isExpanded ? 'dict-table__row--expanded' : '', rowClass(collapsedRow ? collapsedRow.candidates : [])]">
        <td class="dict-table__cell dict-table__cell--disclosure">
          <button
            ref="disclosureButton"
            type="button"
            class="dict-table__expand dict-table__expand--desktop"
            :aria-expanded="String(isExpanded)"
            :aria-controls="detailsId"
            :aria-label="$t(isExpanded ? 'dict.hide_details' : 'dict.show_details')"
            @click="toggleExpanded"
          >
            <i
              class="fas fa-chevron-right dict-table__expand-icon"
              :class="{ 'dict-table__expand-icon--open': isExpanded }"
              aria-hidden="true"
            ></i>
          </button>
        </td>
        <td class="dict-table__cell dict-table__cell--form" :data-label="$t('dict.for_label')">
          <div :id="detailsId" class="dict-table__mapping-stack">
            <template v-if="isExpanded">
              <div
                v-for="row in rows"
                :key="entry.id + '-form-' + row.key"
                :class="['dict-table__mapping-line', rowClass(row.candidates)]"
              >
                <span class="dict-table__scope" aria-hidden="true">
                  <i :class="row.icon"></i>
                </span>
                <span class="dict-word-list">
                  <template v-for="(word, idx) in row.genderedWords">
                    <router-link
                      :key="row.key + '-form-word-' + idx"
                      :to="encodeURI('/dict/' + word.link)"
                      replace
                      :class="wordClass(word.candidates)"
                    >{{ word.text }}</router-link>
                    <span
                      v-if="idx < row.genderedWords.length - 1"
                      :key="row.key + '-form-sep-' + idx"
                      class="dict-word-sep"
                    > · </span>
                  </template>
                </span>
                <span class="sr-only" v-if="scopeLabel(row)">{{ scopeLabel(row) }}</span>
              </div>
            </template>
            <span v-else-if="collapsedRow" class="dict-table__form-inner">
              <span class="dict-word-list">
                <template v-for="(word, idx) in collapsedRow.genderedWords">
                  <router-link
                    :key="collapsedRow.key + '-word-' + idx"
                    :to="encodeURI('/dict/' + word.link)"
                    replace
                    :class="wordClass(word.candidates)"
                  >{{ word.text }}</router-link>
                  <span
                    v-if="idx < collapsedRow.genderedWords.length - 1"
                    :key="collapsedRow.key + '-sep-' + idx"
                    class="dict-word-sep"
                  > · </span>
                </template>
              </span>
            </span>
          </div>
        </td>
        <td class="dict-table__cell dict-table__cell--alt" :data-label="alternativeLabel">
          <div class="dict-table__mapping-stack dict-table__mapping-stack--alt">
            <template v-if="isExpanded">
              <div
                v-for="row in rows"
                :key="entry.id + '-alt-' + row.key"
                :class="['dict-table__mapping-line', rowClass(row.candidates)]"
              >
                <span class="dict-table__scope" aria-hidden="true">
                  <i :class="row.icon"></i>
                </span>
                <span
                  :class="[
                    'dict-table__answer',
                    row.isNotation ? 'dict-table__answer--notation' : ''
                  ]"
                >{{ row.alternativeText }}</span>
              </div>
            </template>
            <span
              v-else-if="collapsedRow"
              :class="[
                'dict-table__answer',
                collapsedRow.isNotation ? 'dict-table__answer--notation' : ''
              ]"
            >{{ collapsedRow.alternativeText }}</span>
          </div>
        </td>
        <td class="dict-table__cell dict-table__cell--votes">
          <div class="dict-vote dict-vote--table" v-if="isNeutralEntry(entry)">
            <button
              type="button"
              class="dict-vote__btn dict-vote__btn--up"
              :class="{ 'dict-vote__btn--active': entry.like_by_me }"
              :title="entry.like_by_me ? $t('this entry is useful (click again to undo)') : $t('this entry is useful')"
              v-b-tooltip.hover.top
              v-on="isLoggedIn() ? { click: () => $emit('like', entry.id) } : { click: () => $emit('register') }"
            >
              <i :class="entry.like_by_me ? 'fas fa-caret-up' : 'fa fa-angle-up'"></i>
            </button>
            <span class="dict-vote__score">{{ entry.total_likes - entry.total_dislikes }}</span>
            <button
              type="button"
              class="dict-vote__btn"
              :class="{ 'dict-vote__btn--active': entry.dislike_by_me }"
              :title="entry.dislike_by_me ? $t('this entry is not useful (click again to undo)') : $t('this entry is not useful')"
              v-b-tooltip.hover.top
              v-on="isLoggedIn() ? { click: () => $emit('dislike', entry.id) } : { click: () => $emit('register') }"
            >
              <i :class="entry.dislike_by_me ? 'fas fa-caret-down text-secondary' : 'fa fa-angle-down text-secondary'"></i>
            </button>
          </div>
          <div v-else-if="isNotationEntry(entry)" class="dict-notation-hint dict-notation-hint--table">
            <i
              class="fas fa-info-circle dict-notation-hint__icon"
              :title="$t('dict.notation_tooltip')"
              v-b-tooltip.hover.top
            ></i>
          </div>
        </td>
        <td class="dict-table__cell dict-table__cell--comment" :data-label="$t('dict.table.comment')">
          <span
            v-if="hasExample"
            :class="['dict-table__comment', isExpanded ? 'dict-table__comment--expanded' : '']"
            v-b-tooltip.hover.top
            :title="isExpanded ? '' : entry.example"
          >
            <i class="fas fa-quote-left dict-table__comment-icon" aria-hidden="true"></i>
            <span
              :class="['dict-table__comment-text', isExpanded ? 'dict-table__comment-text--expanded' : '']"
            >{{ entry.example }}</span>
          </span>
        </td>
        <td class="dict-table__cell dict-table__cell--details" :data-label="$t('dict.table.details')">
          <div class="dict-table__details">
            <div class="dict-table__meta">
              <router-link
                :to="{ name: 'auth.profile', params: { username: entry.editor } }"
                v-b-tooltip.hover.top
                :title="entry.edit_date || ''"
              >
                <i class="far fa-user dict-table__meta-icon" aria-hidden="true"></i>
                {{ entry.editor }}
              </router-link>
            </div>
            <dict-entry-actions
              v-if="isExpanded"
              class="dict-table__inline-actions"
              :entry="entry"
              @add="$emit('add', $event)"
              @flag="$emit('flag', $event)"
              @register="$emit('register')"
            />
          </div>
        </td>
      </tr>
    </template>
  </tbody>
</template>

<script>
import { modalMixin } from '../mixins/modalMixin'
import { dictionaryEntryDisplayMixin } from '../mixins/dictionaryEntryDisplayMixin'
import { collapsedMappingRow, entryMappingRows } from '../utils/genderedFormLabel'
import DictEntryActions from './DictEntryActions'

export default {
  name: 'DictEntryTableGroup',
  components: { DictEntryActions },
  mixins: [modalMixin, dictionaryEntryDisplayMixin],
  props: {
    entry: { type: Object, required: true },
    parentSearchTerm: { default: '' },
    alternativeLabel: { type: String, required: true },
    narrowTable: { type: Boolean, default: false }
  },
  data () {
    return {
      isExpanded: false
    }
  },
  computed: {
    rows () {
      return entryMappingRows(this.entry, { placeholderChar: this.placeholderChar })
    },
    collapsedRow () {
      return collapsedMappingRow(this.rows, this.resolvedSearchTerm)
    },
    hasCreator () {
      return Boolean(this.entry.editor)
    },
    detailsId () {
      return `dict-entry-details-${this.entry.id}`
    },
    hasExample () {
      return this.isNeutralEntry(this.entry) && this.entry.example
    },
    resolvedSearchTerm () {
      const t = this.parentSearchTerm
      if (typeof t === 'string') return t
      if (t && t.code && t.code.c) return t.code.c
      return ''
    }
  },
  watch: {
    resolvedSearchTerm () {
      this.isExpanded = false
    }
  },
  methods: {
    toggleExpanded () {
      const restoreFocus = this.$refs.disclosureButton === document.activeElement
      this.isExpanded = !this.isExpanded
      if (restoreFocus) {
        this.$nextTick(() => {
          if (this.$refs.disclosureButton) this.$refs.disclosureButton.focus()
        })
      }
    },
    scopeLabel (row) {
      if (row.key === 'singular') return this.$t('dict.scope_singular')
      if (row.key === 'plural') return this.$t('dict.scope_plural')
      return ''
    },
    matchesSearch (candidates) {
      const term = this.resolvedSearchTerm
      return term && candidates.includes(term)
    },
    wordClass (candidates) {
      return ['dict-word-link', this.matchesSearch(candidates) ? 'dict-word-link--active' : '']
    },
    rowClass (candidates) {
      return this.matchesSearch(candidates) ? 'dict-table__row--highlight' : ''
    }
  }
}
</script>
